import { AlertTriangle, RotateCw } from 'lucide-react';

/**
 * LoadError — โหลดข้อมูลไม่สำเร็จแล้วต้อง "บอก" เสมอ
 *
 * กับดักที่เจอจากลูกค้าจริง: เขียน `if (isLoading || !data) return <Loading/>`
 * พอ react-query error จะได้ `isLoading=false` + `data=undefined` ซึ่งตกมาเข้า
 * เงื่อนไข `!data` → สปินเนอร์หมุนค้างตลอดไป ไม่มีข้อความสักตัว ลูกค้าเห็นเป็น
 * "ระบบพัง" ทั้งที่จริงคือสิทธิ์ไม่ถึง (403) หรือเน็ตหลุด.
 *
 * กติกา: ทุกหน้าที่โหลดข้อมูลต้องแยก `if (isLoading)` กับ `if (error || !data)`
 * แล้วให้กิ่งหลังคืนคอมโพเนนต์นี้ — ห้ามรวมสองเงื่อนไขเป็นสปินเนอร์ก้อนเดียว.
 */
export function LoadError({
  error,
  hint,
  onRetry,
}: {
  error?: unknown;
  /** ทางออกของหน้านี้โดยเฉพาะ เช่น "เวลาเข้า–ออกของแต่ละกะแก้ได้ที่แท็บ กะ" */
  hint?: string;
  onRetry?: () => void;
}) {
  // ApiError พก status จริงมาด้วย — แยก 403 (สิทธิ์ไม่ถึง) ออกจาก error อื่น
  // เพราะสองอย่างนี้ผู้ใช้ต้องทำคนละอย่าง: ไปหาเจ้าของ vs กดลองใหม่
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? Number((error as { status: unknown }).status)
      : 0;
  const forbidden = status === 403;
  const msg = error instanceof Error && error.message ? error.message : 'โหลดข้อมูลไม่สำเร็จ';

  return (
    <div className="card p-4 text-sm max-w-xl">
      <div className="flex items-center gap-2 font-semibold text-red-600 mb-1">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        {forbidden ? 'บัญชีนี้ไม่มีสิทธิ์ดูส่วนนี้' : 'โหลดข้อมูลไม่สำเร็จ'}
      </div>
      <div className="text-gray-600">
        {forbidden ? 'ให้เจ้าของหรือผู้ดูแลระบบเป็นคนตั้งค่าส่วนนี้ให้' : msg}
      </div>
      {hint && <div className="text-gray-600 mt-1">{hint}</div>}
      {!forbidden && (
        <button
          onClick={onRetry ?? (() => window.location.reload())}
          className="btn-secondary mt-3 inline-flex items-center gap-1"
        >
          <RotateCw className="w-4 h-4" /> ลองใหม่
        </button>
      )}
    </div>
  );
}
